import React, { Fragment, useMemo } from 'react';
import { formatCurrency } from '../../../lib/format';
import { useDefaultCurrency } from '../../../state/zustand';
import { TableRow, TableCell } from '../../ui';
import { ParamIndicator } from './ParamIndicator';
import { countTotalMassDistance } from '../../core/Core.voyage-metrics';
import type { ScheduleDataContext } from '../../core/ScheduleDataContext';
import type { EventTimeline } from '../../core/Timelines';
import {
  useGetCo2EmissionsByShipmentId,
  useGetEuEtsCostsByShipmentId,
} from '../../pnl/ui/context';
import { sumBy } from 'lodash';
import { createPnlSubRows, SummaryRow } from './SummaryRow';
import type { ClientId } from '@seaber/common/lib/clientId';
import { DEFAULT_CARBON_FACTOR } from '../ets';

export function Co2Emissions({
  timeline,
  externalValuesVisible,
  scheduleContext,
  shipmentIds,
}: {
  timeline: EventTimeline;
  externalValuesVisible?: boolean;
  scheduleContext: ScheduleDataContext | undefined;
  shipmentIds: Set<ClientId>;
}) {
  const getCo2EmissionsByShipment = useGetCo2EmissionsByShipmentId();

  const { bunkerUsageByGrade, totalCO2 } = useMemo(() => {
    let totalCO2 = 0;
    const bunkerUsageByGrade: Record<
      string,
      {
        carbonFactor: number;
        consumedBunkerTonnes: number;
      }
    > = {};

    for (const shipmentId of shipmentIds) {
      const emissions = getCo2EmissionsByShipment(shipmentId);
      const co2 = sumBy(emissions, (e) => e.emittedCo2Tonnes);
      totalCO2 += co2;

      for (const emission of emissions) {
        const { consumption } = emission;
        const cur = bunkerUsageByGrade[consumption.bunkerGrade] ?? {
          carbonFactor: DEFAULT_CARBON_FACTOR,
          consumedBunkerTonnes: 0,
        };

        bunkerUsageByGrade[consumption.bunkerGrade] = {
          carbonFactor: emission.carbonFactor,
          consumedBunkerTonnes:
            cur.consumedBunkerTonnes + consumption.consumedBunkerTonnes,
        };
      }
    }

    return {
      bunkerUsageByGrade,
      totalCO2,
    };
  }, [getCo2EmissionsByShipment, shipmentIds]);

  const distances = scheduleContext?.distances;
  const tonMiles = useMemo(() => {
    if (!distances) {
      return 0;
    }
    let total = 0;
    timeline.voyageGroups.forEach(
      (vg) => (total += countTotalMassDistance(vg.events, distances)),
    );
    return total;
  }, [timeline, distances]);

  const co2GramsPerTonMile =
    tonMiles > 0 ? (1_000_000 * totalCO2) / tonMiles : 0;

  return (
    <>
      {Object.entries(bunkerUsageByGrade).map(([grade, consumption]) => {
        const tCO2 =
          consumption.consumedBunkerTonnes * consumption.carbonFactor;

        return (
          <Fragment key={grade}>
            <TableRow>
              <TableCell>CO2 emission</TableCell>
              <TableCell data-testid="CO2-emission-value">
                {tCO2.toFixed(2)} t-CO2
              </TableCell>
              {externalValuesVisible && (
                <>
                  <TableCell /> <TableCell />
                </>
              )}
              <TableCell>
                <ParamIndicator
                  param={{
                    testId: 'CO2-emission-estimated-bunker-tons',
                    name: 'Estimated bunkers used (tons)',
                    value: consumption.consumedBunkerTonnes,
                    source: 'SEABER',
                  }}
                />
                <ParamIndicator
                  param={{
                    testId: 'CO2-emission-grade',
                    name: 'Grade',
                    value: grade,
                    source: 'SEABER',
                  }}
                />
                <ParamIndicator
                  param={{
                    testId: 'CO2-emission-t-co2-t-fuel',
                    name: 't-CO2 / t-Fuel',
                    value: consumption.carbonFactor,
                    source: 'SEABER',
                  }}
                />
              </TableCell>
            </TableRow>
          </Fragment>
        );
      })}
      <TableRow>
        <TableCell>EEOI</TableCell>
        <TableCell data-testid="EEOI-value">
          {co2GramsPerTonMile.toFixed(2)} grams per ton-mile
        </TableCell>
        {externalValuesVisible && (
          <>
            <TableCell /> <TableCell />
          </>
        )}
        <TableCell>
          <ParamIndicator
            param={{
              testId: 'EEOI-t-CO2',
              name: 't-CO2',
              value: totalCO2,
              source: 'SEABER',
            }}
          />
          <ParamIndicator
            param={{
              testId: 'EEOI-Ton-miles',
              name: 'Ton-miles',
              value: tonMiles,
              source: 'SEABER',
            }}
          />
        </TableCell>
      </TableRow>
    </>
  );
}

export function EuEtsCosts({
  externalValuesVisible,
  isVisible,
  shipmentIds,
}: {
  externalValuesVisible: boolean;
  isVisible: boolean;
  shipmentIds: Set<ClientId>;
}) {
  if (!isVisible) return null;
  return Array.from(shipmentIds).map((shipmentId) => (
    <ShipmentEuEtsCost
      key={shipmentId}
      shipmentId={shipmentId}
      externalValuesVisible={externalValuesVisible}
    />
  ));
}

function ShipmentEuEtsCost({
  externalValuesVisible,
  shipmentId,
}: {
  externalValuesVisible: boolean;
  shipmentId: ClientId;
}) {
  const getEuEtsCostsByShipment = useGetEuEtsCostsByShipmentId();
  const euEtsCosts = getEuEtsCostsByShipment(shipmentId).filter(
    (cost) => cost.etsFactor > 0,
  );
  const defaultCurrency = useDefaultCurrency();

  if (euEtsCosts.length === 0) return null;

  const total = sumBy(euEtsCosts, (cost) => cost.amount);

  const subRows = createPnlSubRows(euEtsCosts, {
    baseCurrency: defaultCurrency,
    keyFn: ({ locations, portCallId }) =>
      `${locations.map((loc) => loc.name).join(' - ')}-${portCallId ?? ''}`,
    labelFn: (costs) =>
      `${costs[0].locations.map((loc) => loc.name).join(' - ')}`,
    showExternalValues: externalValuesVisible,
    paramFn: (costs) => {
      // We assume that each leg/port call has one EU ETS cost entry
      const cost = costs[0];

      return [
        {
          testId: 'EU-ETS-t-CO2',
          name: 't-CO2',
          value: cost.totalCo2Tonnes,
          source: 'SEABER',
        },
        {
          testId: 'EU-ETS-int-ext-eu-factor',
          name: 'Intra/Extra-EU factor',
          value: cost.etsFactor,
          source: 'SEABER',
        },
      ];
    },
  });

  const firstCost = euEtsCosts[0];

  return (
    <SummaryRow
      currency={defaultCurrency}
      name="EU ETS Cost"
      subRows={subRows}
      value={total}
      isHiddenIfNull
      externalValue={externalValuesVisible ? null : undefined}
      params={[
        {
          testId: 'EU-ETS-carbon-tonne-price',
          name: 'Carbon tonne price',
          value: formatCurrency(
            firstCost.carbonPricePerTonne,
            2,
            defaultCurrency,
          ),
          source: 'SEABER',
        },
        {
          testId: 'EU-ETS-price-from',
          name: 'Price from',
          value: firstCost.isPriceOverride ? 'Override' : 'Cached',
          source: 'SEABER',
        },
        ...(firstCost.priceUpdatedAt
          ? [
              {
                testId: 'EU-ETS-price-updated-at',
                name: 'Price updated at',
                value: new Date(firstCost.priceUpdatedAt).toLocaleString(),
                source: 'SEABER',
              } as const,
            ]
          : []),
        {
          testId: 'EU-ETS-percentage-factor',
          name: `Percentage factor (${firstCost.year})`,
          value: `${firstCost.transitionFactor * 100}%`,
          source: 'SEABER',
        },
      ]}
    />
  );
}
